/**
 * Select Component
 *
 * A comprehensive select/dropdown component using the KRP Design System.
 * Supports validation states, icons, and various sizes.
 *
 * @example
 * <Select label="Company" options={[{ value: '1', label: 'Company A' }]} />
 * <Select label="Status" error="กรุณาเลือกสถานะ" required />
 */

import React, { SelectHTMLAttributes, forwardRef, useId } from 'react';
import { RiErrorWarningLine, RiAlertLine, RiCheckLine, RiArrowDownSLine } from 'react-icons/ri';
import { cn } from '../../utils/cn';

// ============================================================================
// TYPES
// ============================================================================

export type SelectSize = 'sm' | 'md' | 'lg';
export type SelectState = 'default' | 'error' | 'warning' | 'success';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Select label */
  label?: string;
  /** Size variant */
  size?: SelectSize;
  /** Validation state */
  state?: SelectState;
  /** Error message */
  error?: string | null;
  /** Warning message */
  warning?: string | null;
  /** Success message */
  success?: string | null;
  /** Helper text */
  helperText?: string;
  /** Mark as required */
  required?: boolean;
  /** Left icon */
  leftIcon?: React.ReactNode;
  /** Options array */
  options?: SelectOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Full width */
  fullWidth?: boolean;
  /** Container class names */
  containerClassName?: string;
  /** Select class names */
  selectClassName?: string;
}

// ============================================================================
// SIZE STYLES
// ============================================================================

const sizeStyles: Record<SelectSize, string> = {
  sm: 'h-8 px-2.5 pr-8 text-sm',
  md: 'h-10 px-3 pr-10 text-sm',
  lg: 'h-12 px-4 pr-12 text-base',
};

const labelSizeStyles: Record<SelectSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

const iconSizeStyles: Record<SelectSize, string> = {
  sm: 'right-2',
  md: 'right-3',
  lg: 'right-4',
};

// ============================================================================
// STATE STYLES
// ============================================================================

const stateStyles: Record<SelectState, string> = {
  default: cn(
    'border-secondary-300',
    'focus:border-primary-500 focus:ring-primary-500'
  ),
  error: cn(
    'border-danger-300',
    'focus:border-danger-500 focus:ring-danger-500'
  ),
  warning: cn(
    'border-warning-300',
    'focus:border-warning-500 focus:ring-warning-500'
  ),
  success: cn(
    'border-success-300',
    'focus:border-success-500 focus:ring-success-500'
  ),
};

// ============================================================================
// STATE ICONS
// ============================================================================

const StateIcon: React.FC<{ state: SelectState; className?: string }> = ({
  state,
  className,
}) => {
  switch (state) {
    case 'error':
      return <RiErrorWarningLine className={cn('text-danger-500', className)} />;
    case 'warning':
      return <RiAlertLine className={cn('text-warning-500', className)} />;
    case 'success':
      return <RiCheckLine className={cn('text-success-500', className)} />;
    default:
      return null;
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      size = 'md',
      state: propState,
      error,
      warning,
      success,
      helperText,
      required = false,
      leftIcon,
      options,
      placeholder,
      fullWidth = true,
      containerClassName,
      selectClassName,
      disabled,
      children,
      className,
      id: propId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const selectId = propId || generatedId;
    const errorId = `${selectId}-error`;

    // Determine state from props
    const state: SelectState = propState || (error ? 'error' : warning ? 'warning' : success ? 'success' : 'default');
    const hasMessage = error || warning || success || helperText;

    // Get message text and color
    const getMessage = () => {
      if (error) return { text: error, color: 'text-danger-600' };
      if (warning) return { text: warning, color: 'text-warning-600' };
      if (success) return { text: success, color: 'text-success-600' };
      if (helperText) return { text: helperText, color: 'text-secondary-500' };
      return null;
    };

    const message = getMessage();

    return (
      <div className={cn(fullWidth && 'w-full', containerClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={selectId}
            className={cn(
              'block font-medium text-secondary-700 mb-1.5',
              labelSizeStyles[size]
            )}
          >
            {label}
            {required && (
              <span className="text-danger-500 ml-0.5">*</span>
            )}
          </label>
        )}

        {/* Select wrapper */}
        <div className="relative">
          {/* Left icon */}
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-secondary-400 w-5 h-5">{leftIcon}</span>
            </div>
          )}

          {/* Select */}
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            // Note: Do NOT use required={required} as it triggers HTML5 native tooltip
            // Use inline error messages instead for consistent UX
            aria-required={required}
            aria-invalid={state === 'error'}
            aria-describedby={hasMessage ? errorId : undefined}
            className={cn(
              // Base styles
              'block w-full rounded-md border bg-white shadow-input',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'appearance-none cursor-pointer',
              // Size
              sizeStyles[size],
              // State
              stateStyles[state],
              // Disabled
              disabled && 'bg-secondary-100 text-secondary-500 cursor-not-allowed',
              // Left icon padding
              leftIcon && 'pl-10',
              // Custom class
              selectClassName,
              className
            )}
            {...props}
          >
            {/* Placeholder option */}
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {/* Options from array */}
            {options?.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
            {/* Or children */}
            {children}
          </select>

          {/* Right side: State icon + Arrow */}
          <div className={cn('absolute inset-y-0 flex items-center gap-1 pointer-events-none', iconSizeStyles[size])}>
            {/* State icon */}
            {state !== 'default' && (
              <StateIcon state={state} className="w-4 h-4" />
            )}
            {/* Arrow icon */}
            <RiArrowDownSLine className="w-5 h-5 text-secondary-400" />
          </div>
        </div>

        {/* Message */}
        {message && (
          <div
            id={errorId}
            className={cn('flex items-center gap-1 text-xs mt-1.5', message.color)}
          >
            {(state === 'error' || state === 'warning') && (
              <StateIcon state={state} className="w-3.5 h-3.5 flex-shrink-0" />
            )}
            <span>{message.text}</span>
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

// ============================================================================
// EXPORTS
// ============================================================================

export default Select;
